import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { WorkspaceConfig } from "./config";

export type Role = "business_user" | "procurement_user" | "sme" | "template_publisher" | "agent_configurator" | "admin";
export interface WorkspaceUser { sub: string; email: string | null; name: string | null; roles: Role[]; agents: string[]; tenant_id: string | null; sme_domains: string[] }

export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: WorkspaceUser | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  mode: "auth0" | "stub";
  /** Sign in with a token minted by scripts/dev_token.py: the only way in under stub mode, and the small second
   *  way in under Auth0 when the deployment allows it. */
  loginWithToken?: (token: string) => void;
  /** Auth0 came back with an error, or the code exchange failed: the page offers "Try again". */
  signInFailed?: boolean;
  /** The deployment still accepts a pasted token: the sign-in page offers it as a small second way in. */
  /** Stub mode: the stored token had expired and was dropped; the sign-in page says so. */
  sessionExpired?: boolean;
}

const AuthContext = createContext<AuthState | null>(null);
const ConfigContext = createContext<WorkspaceConfig | null>(null);

export class TokenExpiredError extends Error {}

/** True when the JWT carries an `exp` in the past. */
export function isExpired(token: string, now = Date.now()): boolean {
  try {
    const part = token.split(".")[1];
    const payload = JSON.parse(atob((part ?? "").replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 <= now;
  } catch { return false; }
}

export function decodeClaims(token: string, ns: string): WorkspaceUser {
  const part = token.split(".")[1];
  if (!part) throw new Error("not a JWT");
  const payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
  if (typeof payload.exp === "number" && payload.exp * 1000 <= Date.now()) throw new TokenExpiredError("token expired");
  // the configured namespace first, then a plain claim, then any namespace: a token minted under a different prefix
  // must not read as "no access" here when the API, which is the guard, accepts it
  const claim = (k: string): unknown => {
    if (payload[ns + k] !== undefined) return payload[ns + k];
    if (payload[k] !== undefined) return payload[k];
    const key = Object.keys(payload).find((p) => p.endsWith("/" + k));
    return key === undefined ? undefined : payload[key];
  };
  const list = (k: string) => { const v = claim(k); return (Array.isArray(v) ? v : []) as string[]; };
  const text = (k: string) => { const v = claim(k); return typeof v === "string" && v ? v : null; };
  return { sub: String(payload.sub), email: text("email"), name: text("name") ?? (typeof payload.given_name === "string" ? payload.given_name : null), roles: list("roles") as Role[], agents: list("agents"), tenant_id: text("tenant_id"), sme_domains: list("sme_domains") };
}

// ---- Auth0 mode ---------------------------------------------------------------------

function Auth0Bridge({ config, children, onUseToken }: { config: WorkspaceConfig; children: ReactNode; onUseToken: (t: string) => void }) {
  const { isLoading, isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently, error } = useAuth0();
  const [user, setUser] = useState<WorkspaceUser | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setUser(null); return; }
    getAccessTokenSilently()
      .then((t) => { setUser(decodeClaims(t, config.claims_namespace)); setFailed(false); })
      .catch((e) => { console.warn("sign-in: token exchange failed", e);  // the code stays in the console, never on the page
        setUser(null); setFailed(true); });
  }, [isAuthenticated, getAccessTokenSilently, config.claims_namespace]);
  useEffect(() => { if (error) { console.warn("sign-in: Auth0 returned an error", error); setFailed(true); } }, [error]);
  const value = useMemo<AuthState>(() => ({
    isLoading: isLoading || (isAuthenticated && !user),
    isAuthenticated,
    user,
    login: () => void loginWithRedirect({ appState: { returnTo: window.location.pathname } }),
    logout: () => void logout({ logoutParams: { returnTo: window.location.origin } }),
    getAccessToken: () => getAccessTokenSilently(),
    mode: "auth0",
    loginWithToken: config.token_signin ? onUseToken : undefined,   // Auth0: only where the deployment keeps that way in
    signInFailed: failed,
  }), [isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently, config.token_signin, onUseToken, failed]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---- Stub mode (local dev, Playwright): a token from the local signer in localStorage -----

export const STUB_TOKEN_KEY = "workspace.stub_token";
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private window: the token lives for this page only */ } };
const drop = () => { try { localStorage.removeItem(STUB_TOKEN_KEY); } catch { /* nothing stored */ } };

function StubBridge({ config, children, token, setToken }: { config: WorkspaceConfig; children: ReactNode; token: string | null; setToken: (t: string | null) => void }) {
  const [expired, setExpired] = useState(false);
  const user = useMemo(() => {
    if (!token) return null;
    try { return decodeClaims(token, config.claims_namespace); }
    catch (e) { drop(); if (e instanceof TokenExpiredError) setExpired(true); return null; }  // a bad or expired token is dropped, not fatal
  }, [token, config.claims_namespace]);
  // a token that expires mid-session: every API call would 401 while the page looks signed in, so check each minute
  useEffect(() => {
    if (!token) return;
    const t = window.setInterval(() => { if (isExpired(token)) { setExpired(true); setToken(null); } }, 60_000);
    return () => window.clearInterval(t);
  }, [token, setToken]);
  const loginWithToken = useCallback((t: string) => { setExpired(false); setToken(t.trim()); }, [setToken]);
  const login = useCallback(() => { /* the SignIn page renders a token field in stub mode */ }, []);
  const value = useMemo<AuthState>(() => ({
    isLoading: false, isAuthenticated: !!user, user, login,
    logout: () => { setExpired(false); setToken(null); },
    getAccessToken: async () => { if (!token) throw new Error("not authenticated"); return token; },
    mode: "stub",
    loginWithToken,
    sessionExpired: expired,
  }), [user, token, login, loginWithToken, expired, setToken]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function WorkspaceAuthProvider({ config, children }: { config: WorkspaceConfig; children: ReactNode }) {
  // a pasted token, when there is one, drives the session in either mode: it is the way back in when Auth0 cannot be used
  const [token, setTokenState] = useState<string | null>(() => read(STUB_TOKEN_KEY));
  const setToken = useCallback((t: string | null) => { if (t) write(STUB_TOKEN_KEY, t); else drop(); setTokenState(t); }, []);
  const inner = config.auth_mode === "stub" || token ? <StubBridge config={config} token={token} setToken={setToken}>{children}</StubBridge> : (
    <Auth0Provider
      domain={config.auth0_domain}
      clientId={config.auth0_client_id}
      authorizationParams={{ audience: config.auth0_audience, redirect_uri: window.location.origin, scope: "openid profile email offline_access" }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={(appState) => { window.history.replaceState({}, "", (appState as { returnTo?: string } | undefined)?.returnTo || "/"); }}
    >
      <Auth0Bridge config={config} onUseToken={setToken}>{children}</Auth0Bridge>
    </Auth0Provider>
  );
  return <ConfigContext.Provider value={config}>{inner}</ConfigContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside WorkspaceAuthProvider");
  return ctx;
}
export function useUser(): WorkspaceUser | null { return useAuth().user; }
export function useConfig(): WorkspaceConfig {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig outside WorkspaceAuthProvider");
  return ctx;
}
export const TestAuthContext = AuthContext;
export const TestConfigContext = ConfigContext;
